import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Input, Card, DateTimePicker, RadioButton } from '../../../../shared/custom-ui';
import {
    EnumPatientInfo,
    PATIENT_INFO_OPTIONS,
    EnumGender,
    IPatientInfo,
} from '../../interfaces';
import useValidation, { IError, type ValidationRule } from '../../../../hooks/formValidation/useValidation';

const defaultPatientObj: IPatientInfo = {
    [EnumPatientInfo.PatientFirstName]: '',
    [EnumPatientInfo.PatientMiddleName]: '',
    [EnumPatientInfo.PatientLastName]: '',
    [EnumPatientInfo.Date]: new Date().toISOString().split('T')[0],
    [EnumPatientInfo.Gender]: '',
    [EnumPatientInfo.Dob]: '',
    [EnumPatientInfo.ParentGuardianName]: '',
    [EnumPatientInfo.PrimaryInsurance]: '',
    [EnumPatientInfo.PrimaryAuthNumber]: '',
    [EnumPatientInfo.Contact]: '',
    [EnumPatientInfo.Address]: '',
};

type ValidationRuleEntry = (ValidationRule | 'required' | 'phone-format' | 'email-format')[];
const validationPatientObj: Record<EnumPatientInfo, ValidationRuleEntry> = {
    [EnumPatientInfo.PatientFirstName]: ['required'],
    [EnumPatientInfo.PatientMiddleName]: [],   // optional
    [EnumPatientInfo.PatientLastName]: [],     // optional
    [EnumPatientInfo.Date]: ['required'],
    [EnumPatientInfo.Gender]: ['required'],
    [EnumPatientInfo.Dob]: ['required'],
    [EnumPatientInfo.Contact]: ['required', 'phone-format'],
    [EnumPatientInfo.Address]: ['required'],
    // Not required:
    [EnumPatientInfo.ParentGuardianName]: [],
    [EnumPatientInfo.PrimaryInsurance]: [],
    [EnumPatientInfo.PrimaryAuthNumber]: [],
};

const PatientInfo = forwardRef<any, {}>(function PatientInfo(_props, ref) {
    const [patientObj, setPatientObj] = useState<IPatientInfo>(defaultPatientObj);
    const [errors, setErrors] = useState<{ [key in EnumPatientInfo]?: IError }>({});
    const [savingStarted, setSavingStarted] = useState<boolean>(false);
    const { validateField } = useValidation();

    useImperativeHandle(ref, () => ({
        savePatientInfo,
        resetValues,
    }));

    const getEnumValue = (key: EnumPatientInfo) => patientObj[key];
    const getLabel = (key: EnumPatientInfo) => PATIENT_INFO_OPTIONS[key];

    const handleChange = (key: EnumPatientInfo, value: any) => {
        setPatientObj(prev => ({ ...prev, [key]: value }));
        if (savingStarted && key === EnumPatientInfo.Gender) {
            blurValidation(EnumPatientInfo.Gender, value);
        }
    };

    const singleValidation = (key: EnumPatientInfo, value: any) =>
        validateField(value, validationPatientObj[key]);

    const blurValidation = (key: EnumPatientInfo, value: any) => {
        if (!savingStarted) return;
        setErrors(prev => ({ ...prev, [key]: singleValidation(key, value) }));
    };

    const savePatientInfo = () => {
        setSavingStarted(true);
        const _errors = (Object.keys(validationPatientObj) as Array<EnumPatientInfo>).reduce(
            (acc: Record<string, IError>, k) => {
                const err = singleValidation(k, patientObj[k]);
                if (err) acc[k] = err;
                return acc;
            },
            {}
        );
        setErrors(_errors);
        return Object.keys(_errors).length
            ? { status: false, data: null }
            : { status: true, data: patientObj };
    };

    const resetValues = () => {
        setPatientObj({ ...defaultPatientObj });
        setErrors({});
        setSavingStarted(false);
    };

    return (
        <div className="space-y-6">

            {/* ── Name fields row: First (required) · Middle · Last ── */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <Input
                    label={getLabel(EnumPatientInfo.PatientFirstName)}
                    value={getEnumValue(EnumPatientInfo.PatientFirstName)}
                    onChange={e => handleChange(EnumPatientInfo.PatientFirstName, e.target.value)}
                    error={errors[EnumPatientInfo.PatientFirstName]?.msg}
                    onBlur={() =>
                        blurValidation(
                            EnumPatientInfo.PatientFirstName,
                            getEnumValue(EnumPatientInfo.PatientFirstName)
                        )
                    }
                    required
                />

                <Input
                    label={getLabel(EnumPatientInfo.PatientMiddleName)}
                    value={getEnumValue(EnumPatientInfo.PatientMiddleName)}
                    onChange={e => handleChange(EnumPatientInfo.PatientMiddleName, e.target.value)}
                    placeholder="Optional"
                />

                <Input
                    label={getLabel(EnumPatientInfo.PatientLastName)}
                    value={getEnumValue(EnumPatientInfo.PatientLastName)}
                    onChange={e => handleChange(EnumPatientInfo.PatientLastName, e.target.value)}
                    placeholder="Optional"
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Input
                    label={getLabel(EnumPatientInfo.ParentGuardianName)}
                    value={getEnumValue(EnumPatientInfo.ParentGuardianName)}
                    onChange={e => handleChange(EnumPatientInfo.ParentGuardianName, e.target.value)}
                />

                <DateTimePicker
                    label={getLabel(EnumPatientInfo.Dob)}
                    value={getEnumValue(EnumPatientInfo.Dob) && getEnumValue(EnumPatientInfo.Dob)}
                    onChange={newDate => handleChange(EnumPatientInfo.Dob, newDate)}
                    onBlur={() => blurValidation(EnumPatientInfo.Dob, getEnumValue(EnumPatientInfo.Dob))}
                    pickerType="date"
                    error={errors[EnumPatientInfo.Dob]?.msg}
                    required
                />

                <Input
                    label={getLabel(EnumPatientInfo.Contact)}
                    value={getEnumValue(EnumPatientInfo.Contact)}
                    onChange={e => handleChange(EnumPatientInfo.Contact, e.target.value)}
                    onBlur={() =>
                        blurValidation(EnumPatientInfo.Contact, getEnumValue(EnumPatientInfo.Contact))
                    }
                    type="number"
                    error={errors[EnumPatientInfo.Contact]?.msg}
                    required
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                <Input
                    label={getLabel(EnumPatientInfo.Address)}
                    value={getEnumValue(EnumPatientInfo.Address)}
                    onBlur={() =>
                        blurValidation(EnumPatientInfo.Address, getEnumValue(EnumPatientInfo.Address))
                    }
                    error={errors[EnumPatientInfo.Address]?.msg}
                    onChange={e => handleChange(EnumPatientInfo.Address, e.target.value)}
                    required
                />

                <Input
                    label={getLabel(EnumPatientInfo.PrimaryInsurance)}
                    value={getEnumValue(EnumPatientInfo.PrimaryInsurance)}
                    onChange={e => handleChange(EnumPatientInfo.PrimaryInsurance, e.target.value)}
                />

                <Input
                    label={getLabel(EnumPatientInfo.PrimaryAuthNumber)}
                    value={getEnumValue(EnumPatientInfo.PrimaryAuthNumber)}
                    onChange={e => handleChange(EnumPatientInfo.PrimaryAuthNumber, e.target.value)}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Gender block spans remaining columns on larger screens for nicer composition */}
                <div className="md:col-span-1 lg:col-span-2">
                    <div className="mb-2 text-[15px] font-semibold text-[var(--v2-text-primary)]">
                        {getLabel(EnumPatientInfo.Gender)} <span className="text-[var(--v2-danger)]">*</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <RadioButton
                            label="Male"
                            checked={getEnumValue(EnumPatientInfo.Gender) === EnumGender.Male}
                            onChange={() => handleChange(EnumPatientInfo.Gender, EnumGender.Male)}
                        />
                        <RadioButton
                            label="Female"
                            checked={getEnumValue(EnumPatientInfo.Gender) === EnumGender.Female}
                            onChange={() => handleChange(EnumPatientInfo.Gender, EnumGender.Female)}
                        />
                        <RadioButton
                            label="Other"
                            checked={getEnumValue(EnumPatientInfo.Gender) === EnumGender.Other}
                            onChange={() => handleChange(EnumPatientInfo.Gender, EnumGender.Other)}
                        />
                    </div>
                    {errors[EnumPatientInfo.Gender]?.msg && (
                        <p className="mt-2 text-sm text-red-600">{errors[EnumPatientInfo.Gender]?.msg}</p>
                    )}
                </div>
            </div>
        </div>
    );
});

export default PatientInfo;
